import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import LoadingSpinner from '@/components/ui/loading'
import { toastHandler } from '@/components/utils/Toast'
import { useReorderWorkspaces } from '@/services/auth/mutations'
import { keyUser } from '@/services/auth/queries'
import { GripHorizontal } from 'lucide-react'
import { ReactNode, useState, useRef, useEffect } from 'react'
import { DndProvider, useDrag, useDrop } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'

type Props = {
    trigger: ReactNode
    onClose?: () => void
    initialWorkspaces: any[]
    isLoading: boolean
}

const ITEM_TYPE = 'WORKSPACE'

const DraggableWorkspaceItem = ({
    account,
    index,
    moveItem,
    isDefault,
    onSelectDefault,
}: {
    account: any
    index: number
    moveItem: (dragIndex: number, hoverIndex: number) => void
    isDefault: boolean
    onSelectDefault: () => void
}) => {
    const ref = useRef(null)

    const [, drag] = useDrag({
        type: ITEM_TYPE,
        item: { index },
    })

    const [, drop] = useDrop({
        accept: ITEM_TYPE,
        hover(item: { index: number }) {
            if (!ref.current) return
            const dragIndex = item.index
            const hoverIndex = index
            if (dragIndex === hoverIndex) return

            moveItem(dragIndex, hoverIndex)
            item.index = hoverIndex
        },
    })

    drag(drop(ref))

    return (
        <div
            ref={ref}
            className='flex items-center justify-between px-2 py-4 mx-0 text-sm font-medium text-gray-500 cursor-pointer group auto hover:bg-gray-100'
        >
            {account.org?.name}
            <div className='flex items-center gap-2'>
                <Checkbox
                    className={`border-primary-text ${isDefault ? 'block' : 'hidden group-hover:block'}`}
                    checked={isDefault}
                    onCheckedChange={onSelectDefault}
                />
                <GripHorizontal size={18} />
            </div>
        </div>
    )
}

const ReorderWorkspace = ({ trigger, initialWorkspaces, isLoading }: Props) => {
    const [open, setOpen] = useState(false)
    const [workspaces, setWorkspaces] = useState(initialWorkspaces || [])

    const [defaultWorkspace, setDefaultWorkspace] = useState<string>('')

    const { mutate: reorderWorkspaces, isLoading: isReordering } = useReorderWorkspaces({
        refetch: [keyUser],
        onSuccess: () => {
            toastHandler({ message: 'Setting saved!' })
            setOpen(false)
        },
    })

    const moveItem = (dragIndex: number, hoverIndex: number) => {
        const updatedWorkspaces = [...workspaces]
        const draggedItem = updatedWorkspaces[dragIndex]

        updatedWorkspaces.splice(dragIndex, 1)
        updatedWorkspaces.splice(hoverIndex, 0, draggedItem)

        setWorkspaces(updatedWorkspaces)
    }

    const handleSelectDefault = (workspaceName: string) => {
        setDefaultWorkspace(workspaceName)
    }

    const handleSave = () => {
        const payload = {
            data: workspaces.map((account, index) => ({
                id: account.id,
                order: index,
            })),
        }

        reorderWorkspaces(payload)
    }

    useEffect(() => {
        setWorkspaces(initialWorkspaces || [])
    }, [initialWorkspaces])

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger onClick={e => e.stopPropagation()}>{trigger}</DialogTrigger>
            <DialogContent onClick={e => e.stopPropagation()} className='max-w-[600px]'>
                <DialogHeader>
                    <DialogTitle>Reorder Workspace</DialogTitle>
                </DialogHeader>
                <DndProvider backend={HTML5Backend}>
                    <div className='max-h-[350px] overflow-y-auto'>
                        {isLoading ? (
                            <LoadingSpinner />
                        ) : (
                            workspaces.map((account, index) => (
                                <DraggableWorkspaceItem
                                    key={account.id}
                                    account={account}
                                    index={index}
                                    moveItem={moveItem}
                                    isDefault={defaultWorkspace === account.org?.name}
                                    onSelectDefault={() => handleSelectDefault(account.org?.name)}
                                />
                            ))
                        )}
                    </div>
                    <div className='px-2 pt-2 border-t'>
                        <p className='text-sm text-gray-500'>
                            Default workspace:{' '}
                            <span className='font-semibold text-primary-text'>{defaultWorkspace || ''}</span>
                        </p>
                    </div>
                </DndProvider>

                <DialogFooter className='py-2'>
                    <Button onClick={handleSave} loading={isReordering} className='bg-primary-text'>
                        Save
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

export default ReorderWorkspace
